<template>
 <span @click="handleExport" class="excel-export-wrapper">
    <slot></slot>
  </span>
</template>

<script>
export default {
  name: 'ExcelExport',
  props: {
    tableData: {
      type: String,
      default: ''
    },
    tableRef: {
      type: Object,
      default: null
    },
    data: {
      type: Array,
      default: null
    },
    title: {
      type: [String, Array],
      default: null
    },
    footer: {
      type: [String, Array],
      default: null
    },
    fields: {
      type: Object,
      default: null
    },
    filename: {
      type: String,
      default: 'report.xls'
    },
    worksheetName: {
      type: String,
      default: 'Sheet1'
    }
  },
  data() {
    return {
      isExporting: false
    };
  },
  methods: {
    jsonToHtmlTable(data, title, footer, fields) {
      if (!data || !data.length) {
        return '';
      }

      let tableHtml = '';
      const keys = fields || this.getKeysFromData(data);
      const colspan = Object.keys(keys).length;

      // Title
      if (title) {
        if (Array.isArray(title)) {
          title.forEach(t => {
            tableHtml += `<tr><th colspan="${colspan}">${t}</th></tr>`;
          });
        } else {
          tableHtml += `<tr><th colspan="${colspan}">${title}</th></tr>`;
        }
      }

      // Header
      tableHtml += '<thead><tr>';
      for (let key in keys) {
        tableHtml += `<th>${key}</th>`;
      }
      tableHtml += '</tr></thead>';

      // Body
      tableHtml += '<tbody>';
      data.forEach(item => {
        tableHtml += '<tr>';
        for (let key in keys) {
          const field = keys[key];
          const value = this.getValueFromItem(item, field);
          tableHtml += `<td>${this.escapeHtml(value)}</td>`;
        }
        tableHtml += '</tr>';
      });
      tableHtml += '</tbody>';

      // Footer
      if (footer) {
        tableHtml += '<tfoot>';
        if (Array.isArray(footer)) {
          footer.forEach(f => {
            tableHtml += `<tr><td colspan="${colspan}">${f}</td></tr>`;
          });
        } else {
          tableHtml += `<tr><td colspan="${colspan}">${footer}</td></tr>`;
        }
        tableHtml += '</tfoot>';
      }

      return tableHtml;
    },

    getKeysFromData(data) {
      if (this.fields) {
        return this.fields;
      }
      const keys = {};
      if (data && data.length > 0) {
        for (let key in data[0]) {
          keys[key] = key;
        }
      }
      return keys;
    },

    getValueFromItem(item, field) {
      if (typeof field === 'object' && field.field) {
        field = field.field;
      }
      if (typeof field === 'string' && field.includes('.')) {
        const indexes = field.split('.');
        let value = item;
        for (let index of indexes) {
          value = value ? value[index] : '';
        }
        return value || '';
      }
      return item[field] || '';
    },

    escapeHtml(text) {
      if (text == null) return '';
      const div = document.createElement('div');
      div.textContent = text;
      return div.innerHTML.replace(/\n/g, '<br/>');
    },

  xlsTemplate(table, worksheet) {
    return `<html xmlns:o="urn:schemas-microsoft-com:office:office" 
               xmlns:x="urn:schemas-microsoft-com:office:excel" 
               xmlns="http://www.w3.org/TR/REC-html40">
    <head>
      <meta charset="UTF-8">
      <!--[if gte mso 9]>
      <xml>
        <x:ExcelWorkbook>
          <x:ExcelWorksheets>
            <x:ExcelWorksheet>
              <x:Name>${worksheet}</x:Name>
              <x:WorksheetOptions>
                <x:DisplayGridlines/>
                <x:Print>
                  <x:ValidPrinterInfo/>
                </x:Print>
              </x:WorksheetOptions>
            </x:ExcelWorksheet>
          </x:ExcelWorksheets>
        </x:ExcelWorkbook>
      </xml>
      <![endif]-->
      <style>
        @page {
          margin: 0.75in;
        }
        
        body {
          direction: rtl;
          text-align: right;
        }
        
        table {
          border-collapse: collapse;
          font-family: Tahoma, Arial, sans-serif;
          font-size: 11pt;
          width: auto;
          direction: rtl;
          mso-table-lspace: 0pt;
          mso-table-rspace: 0pt;
        }
        
        th {
          background-color: #14397a;
          color: #FFFFFF;
          font-weight: bold;
          text-align: center;
          padding: 8px 15px;
          border: 1px solid #2F5597;
          vertical-align: middle;
          white-space: nowrap;
          font-size: 11pt;
          width: auto;
        }
        
        td {
          padding: 6px 15px;
          border: 1px solid #D0D0D0;
          text-align: right;
          vertical-align: middle;
          background-color: #FFFFFF;
          font-size: 10pt;
          mso-number-format: General;
          width: auto;
        }
        
        tr:nth-child(even) td {
          background-color: #F2F2F2;
        }
        
        tr:hover td {
          background-color: #E7E6E6;
        }
        
        tfoot td {
          font-weight: bold;
          background-color: #BDD7EE;
          border: 1px solid #8EA9DB;
          font-size: 11pt;
          padding: 8px 6px;
        }
        
        td.number {
          text-align: center;
          mso-number-format: "0";
        }
        
        td.currency {
          text-align: center;
          mso-number-format: "#,##0";
        }
        
        td.percentage {
          text-align: center;
          mso-number-format: "0.00%";
        }
        
        td.date {
          text-align: center;
          mso-number-format: "yyyy/mm/dd";
        }
        
        td:empty::after {
          content: "-";
          color: #999;
        }
      </style>
    </head>
    <body>
      <table border="1" cellspacing="0" cellpadding="0">
        ${table}
      </table>
    </body>
  </html>`;
}, 
    handleExport() {
      this.isExporting = true;
      
      try {
        let tableHtml = this.tableData;
        
        if (this.tableRef && this.tableRef.innerHTML) {
          tableHtml = this.tableRef.innerHTML;
        }
        
        if (this.data && this.data.length > 0) {
          tableHtml = this.jsonToHtmlTable(this.data, this.title, this.footer, this.fields);
        }
        
        if (!tableHtml) {
          this.$emit('error', 'هیچ داده‌ای برای خروجی وجود ندارد');
          this.isExporting = false;
          return;
        }
        
        const htmlContent = this.xlsTemplate(tableHtml, this.worksheetName);
        const blob = new Blob(['\ufeff', htmlContent], { 
          type: 'application/vnd.ms-excel;charset=utf-8' 
        });
        
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = this.filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(link.href);
        
        this.$emit('success', 'فایل با موفقیت دانلود شد');
      } catch (error) {
        this.$emit('error', error.message);
      } finally {
        this.isExporting = false;
      }
    }
  }
};
</script>

<style scoped>
.excel-export-wrapper {
  display: inline-block;
  cursor: pointer;

}

.excel-export-wrapper:has(button:disabled) {
  opacity: 0.6;
  cursor: not-allowed;
}

</style>